<template>
  <div class="omashu-ticket-wrap">

    <!-- Ingresso flutuante (mesmo passaporte/efeito da Home, centralizado) -->
    <div
      v-show="phase === 'idle' || phase === 'animating'"
      class="passport-outer"
      :class="passportClass"
      :style="passportStyle"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @pointerdown="onPointerDown"
    >
      <div class="passport-bob">
        <div class="passport-tilt">
          <div ref="passportWrap" class="passport-wrap">
            <canvas ref="passportCanvas" class="passport-canvas"></canvas>
          </div>
        </div>
      </div>
      <p class="ticket-hint">{{ $t('exposicoesPage.ticketHint') }}</p>
    </div>

    <!-- Muros + fundo preto -->
    <div
      v-if="showWallsOverlay"
      class="walls-overlay"
      :class="{ shaking: isShaking }"
    >
      <div class="black-bg" :class="{ visible: blackVisible }"></div>

      <img
        src="@/assets/omashu/muro_esquerda.jpg"
        class="wall wall-left"
        :class="{ 'wall-in': wallsIn }"
        draggable="false"
        alt=""
      />
      <img
        src="@/assets/omashu/muro_direita.jpg"
        class="wall wall-right"
        :class="{ 'wall-in': wallsIn }"
        draggable="false"
        alt=""
      />
    </div>

    <!-- YouTube fullscreen -->
    <div v-if="phase === 'open'" class="youtube-fullscreen">
      <div class="yt-topbar">
        <button class="back-btn" @click="onBack">
          <img src="@/assets/logo.svg" class="back-logo" alt="" draggable="false" />
          <span class="back-label">{{ $t('exposicoesPage.ticketBack') }}</span>
        </button>
      </div>

      <transition name="fade">
        <img
          v-if="showLogo"
          src="@/assets/omashu/omashuland.png"
          class="omashu-intro-logo"
          alt=""
          draggable="false"
        />
      </transition>

      <transition name="fade">
        <iframe
          v-if="showVideo"
          :src="`https://www.youtube.com/embed/f59654RmbIc?autoplay=1`"
          frameborder="0"
          allow="autoplay; encrypted-media; fullscreen"
          allowfullscreen
        ></iframe>
      </transition>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import passportSrc from '@/assets/omashu/passaporte_omashu.jpg'

const emit = defineEmits(['omashu-open', 'omashu-close'])

// ─── Estado da experiência ────────────────────────────────
const phase            = ref('idle')
const isHovered        = ref(false)
const flyDirection     = ref(null)   // 'left' | 'right' | null
const isSnapping       = ref(false)
const isDragging       = ref(false)
const showWallsOverlay = ref(false)
const wallsIn          = ref(false)
const isShaking        = ref(false)
const blackVisible     = ref(false)
const showLogo         = ref(false)
const showVideo        = ref(false)

// ─── Drag state ───────────────────────────────────────────
const dragOffset = reactive({ x: 0, y: 0 })
let pointerStartX  = 0
let pointerStartY  = 0
let hasMoved       = false
const MOVE_THRESH  = 6  // px para distinguir click de drag

// ─── Classes e estilos do passaporte ─────────────────────
const passportClass = computed(() => ({
  hovered:    isHovered.value && !isDragging.value,
  dragging:   isDragging.value,
  snapping:   isSnapping.value,
  'fly-right': flyDirection.value === 'right',
  'fly-left':  flyDirection.value === 'left',
}))

const passportStyle = computed(() => ({
  '--dx': dragOffset.x + 'px',
  '--dy': dragOffset.y + 'px',
}))

// ─── Canvas do passaporte ─────────────────────────────────
const passportCanvas = ref(null)
const passportWrap   = ref(null)
const img = new Image()
let rafId     = null
let startTime = 0

const AMPLITUDE = 8
let _dpr = 1

function syncCanvasSize() {
  const canvas = passportCanvas.value
  const wrap   = passportWrap.value
  if (!canvas || !wrap || !img.naturalWidth) return

  const isMobile = window.innerWidth / window.innerHeight <= 0.75
  _dpr = Math.min(window.devicePixelRatio || 1, 2)
  let displayW, displayH

  if (isMobile) {
    displayW = window.innerWidth * 0.6
    displayH = displayW * (img.naturalHeight / img.naturalWidth)
  } else {
    displayH = window.innerHeight * 0.26
    displayW = displayH * (img.naturalWidth / img.naturalHeight)
  }

  const totalH = displayH + AMPLITUDE * 2
  wrap.style.width  = displayW + 'px'
  wrap.style.height = totalH + 'px'

  canvas.style.width  = displayW + 'px'
  canvas.style.height = totalH + 'px'
  canvas.style.marginTop = '0'
  canvas.width  = Math.round(displayW * _dpr)
  canvas.height = Math.round(totalH * _dpr)
}

function drawFrame(now) {
  const canvas = passportCanvas.value
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  const W = canvas.width
  const H = canvas.height

  ctx.clearRect(0, 0, W, H)

  const elapsed   = now - startTime
  const phase_    = elapsed * 0.001
  const frequency = 0.7
  const strips    = 80
  const stripW    = W / strips

  const pad   = AMPLITUDE * _dpr
  const drawH = H - pad * 2

  for (let i = 0; i < strips; i++) {
    const t    = i / strips
    const yOff = Math.sin(t * Math.PI * 2 * frequency + phase_) * pad

    ctx.drawImage(
      img,
      t * img.naturalWidth,      0,
      img.naturalWidth / strips, img.naturalHeight,
      i * stripW,                pad + yOff,
      Math.ceil(stripW) + 1,     drawH
    )
  }
}

function tick(now) {
  if (phase.value === 'open') { rafId = null; return }
  drawFrame(now)
  rafId = requestAnimationFrame(tick)
}

function startLoop() {
  if (rafId) return
  startTime = performance.now()
  rafId = requestAnimationFrame(tick)
}

// ─── Interações de hover ──────────────────────────────────
function onMouseEnter() {
  if (phase.value === 'idle' && !isDragging.value) isHovered.value = true
}

function onMouseLeave() {
  if (!isDragging.value) isHovered.value = false
}

// ─── Drag: pointer events ─────────────────────────────────
function onPointerDown(e) {
  if (phase.value !== 'idle') return
  e.currentTarget.setPointerCapture(e.pointerId)
  pointerStartX    = e.clientX
  pointerStartY    = e.clientY
  hasMoved         = false
  isDragging.value = true
  isHovered.value  = false
  isSnapping.value = false
}

function onPointerMove(e) {
  if (!isDragging.value) return
  const dx = e.clientX - pointerStartX
  const dy = e.clientY - pointerStartY
  dragOffset.x = dx
  dragOffset.y = dy
  if (Math.hypot(dx, dy) > MOVE_THRESH) hasMoved = true
}

async function onPointerUp(e) {
  if (!isDragging.value) return
  isDragging.value = false

  if (!hasMoved) {
    // Click / tap → voa para o lado mais próximo
    const side = e.clientX < window.innerWidth / 2 ? 'left' : 'right'
    dragOffset.x = 0
    dragOffset.y = 0
    await openGate(side)
  } else {
    // Drag solto → retorna com spring
    isSnapping.value = true
    dragOffset.x = 0
    dragOffset.y = 0
    await wait(520)
    isSnapping.value = false
  }
}

// ─── Abertura do portão ───────────────────────────────────
const afterPaint = () =>
  new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))

async function openGate(side = 'right') {
  if (phase.value !== 'idle') return
  phase.value     = 'animating'
  isHovered.value = false

  // Ingresso voa para o lado indicado
  flyDirection.value = side
  await wait(360)
  flyDirection.value = null

  // Muros entram
  showWallsOverlay.value = true
  await afterPaint()
  wallsIn.value = true

  await wait(800)

  isShaking.value = true
  await wait(600)
  isShaking.value = false

  await wait(1400)

  blackVisible.value = true
  wallsIn.value      = false

  await wait(900)

  phase.value = 'open'
  emit('omashu-open')

  await wait(150)
  showLogo.value = true

  await wait(3000)

  showVideo.value = true
  await wait(700)
  showLogo.value = false
}

async function onBack() {
  showLogo.value  = false
  showVideo.value = false
  phase.value = 'closing'
  await afterPaint()
  wallsIn.value = true

  await wait(800)

  phase.value        = 'idle'
  blackVisible.value = false
  dragOffset.x       = 0
  dragOffset.y       = 0
  startLoop()
  emit('omashu-close')

  await wait(350)

  wallsIn.value = false
  await wait(900)

  showWallsOverlay.value = false
}

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

// ─── Lifecycle ────────────────────────────────────────────
onMounted(() => {
  img.src = passportSrc
  img.onload = () => {
    syncCanvasSize()
    startLoop()
  }
  window.addEventListener('resize', syncCanvasSize)
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
})

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId)
  window.removeEventListener('resize', syncCanvasSize)
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
})
</script>

<style scoped>
.omashu-ticket-wrap {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* ─── Ingresso ────────────────────────────────────────── */
.passport-outer {
  position: relative;
  z-index: 50;
  cursor: grab;
  touch-action: none;
  transform: translate(var(--dx, 0px), var(--dy, 0px));
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  user-select: none;
  pointer-events: all;
  perspective: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Hover: cresce levemente */
.passport-outer.hovered {
  transform: translate(var(--dx, 0px), var(--dy, 0px)) scale(1.04);
}

/* Arrastando: sem transição, cursor grabbing */
.passport-outer.dragging {
  transition: none;
  cursor: grabbing;
}

/* Snap back com spring após drag */
.passport-outer.snapping {
  transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  cursor: grab;
}

/* Voa para a direita */
.passport-outer.fly-right {
  transform: translate(calc(150vw + var(--dx, 0px)), var(--dy, 0px));
  transition: transform 0.35s cubic-bezier(0.4, 0, 1, 1);
}

/* Voa para a esquerda */
.passport-outer.fly-left {
  transform: translate(calc(-150vw + var(--dx, 0px)), var(--dy, 0px));
  transition: transform 0.35s cubic-bezier(0.4, 0, 1, 1);
}

.passport-bob {
  animation: bob 2.5s ease-in-out infinite;
}

.passport-outer.dragging .passport-bob,
.passport-outer.snapping .passport-bob {
  animation: none;
}

.passport-outer.hovered .passport-bob {
  animation: hover-float 3s ease-in-out infinite;
}

.passport-tilt {
  transform: rotateX(7deg) rotateY(-16deg);
  transform-style: preserve-3d;
}

.passport-wrap {
  display: block;
}

.passport-canvas {
  display: block;
  pointer-events: none;
  -webkit-user-drag: none;
  filter: drop-shadow(0 10px 28px rgba(0, 0, 0, 0.6));
}

.ticket-hint {
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-family: "Archivo", sans-serif;
  font-size: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0;
  animation: hint-fade 2.4s ease-in-out infinite;
}

@keyframes hint-fade {
  0%, 100% { opacity: 0.35; }
  50%       { opacity: 0.9; }
}

@keyframes bob {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-12px); }
}

@keyframes hover-float {
  0%   { transform: translateY(0)    rotate(2.5deg); }
  50%  { transform: translateY(-8px) rotate(-2.5deg); }
  100% { transform: translateY(0)    rotate(2.5deg); }
}

/* ─── Overlay dos muros ───────────────────────────────── */
.walls-overlay {
  position: fixed;
  inset: 0;
  z-index: 600;
  overflow: hidden;
  pointer-events: all;
}

.walls-overlay.shaking {
  animation: screen-shake 0.6s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

@keyframes screen-shake {
  0%,  100% { transform: translate(0, 0); }
  10%        { transform: translate(-10px, -5px); }
  20%        { transform: translate(10px,  5px); }
  30%        { transform: translate(-8px, -3px); }
  40%        { transform: translate(8px,  3px); }
  50%        { transform: translate(-5px, -2px); }
  60%        { transform: translate(5px,  2px); }
  70%        { transform: translate(-3px, -1px); }
  80%        { transform: translate(3px,  1px); }
  90%        { transform: translate(0, 0); }
}

/* ─── Fundo preto ─────────────────────────────────────── */
.black-bg {
  position: absolute;
  inset: 0;
  background: #000;
  opacity: 0;
  transition: opacity 0.6s ease;
  z-index: 1;
}

.black-bg.visible {
  opacity: 1;
}

/* ─── Muros ───────────────────────────────────────────── */
.wall {
  position: absolute;
  top: 0;
  height: 100vh;
  width: 50vw;
  object-fit: cover;
  z-index: 2;
}

.wall-left {
  left: 0;
  object-position: right center;
  transform: translateX(-100%);
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.wall-left.wall-in {
  transform: translateX(0);
}

.wall-right {
  right: 0;
  object-position: left center;
  transform: translateX(100%);
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.wall-right.wall-in {
  transform: translateX(0);
}

/* ─── Fade transition ─────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ─── Logo intro ──────────────────────────────────────── */
.omashu-intro-logo {
  position: absolute;
  width: 22vw;
  max-width: 320px;
  min-width: 160px;
  height: auto;
  object-fit: contain;
  pointer-events: none;
  -webkit-user-drag: none;
}

@media (max-aspect-ratio: 3/4) {
  .omashu-intro-logo {
    width: 55vw;
  }
}

/* ─── YouTube fullscreen ──────────────────────────────── */
.youtube-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 700;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: all;
}

.yt-topbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 16px 24px;
  z-index: 10;
}

.back-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.back-btn:hover {
  opacity: 1;
  transform: scale(1.06);
}

.back-logo {
  height: 36px;
  width: auto;
  display: block;
  pointer-events: none;
  -webkit-user-drag: none;
  filter: brightness(0) invert(1);
}

.back-label {
  color: #fff;
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-left: 10px;
  opacity: 0.85;
  pointer-events: none;
}

.youtube-fullscreen iframe {
  width: 80vw;
  height: 45vw;
  max-height: 80vh;
  max-width: 142vh;
  border: none;
}

@media (max-aspect-ratio: 3/4) {
  .back-logo { height: 26px; }
  .yt-topbar { padding: 12px 16px; }
}
</style>
